import React, { useEffect } from 'react';

import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  createStyles,
  Group,
  Loader,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure, useShallowEffect } from '@mantine/hooks';
import { IconDeviceDesktop, IconLock, IconPlus, IconX } from '@tabler/icons-react';
import ExpandableSection from 'components/ExpandableSection';
import WorkoutRichTextEditor from 'components/workout-program/WorkoutRichTextEditor';
import { useAuth } from 'context/auth-context';
import dayjs from 'dayjs';
import { useScrollContainer } from 'hooks/useScrollContainer';
import useEditAssignedWorkout from 'hooks/workout/useEditAssignedWorkout';
import useWorkout from 'hooks/workout/useWorkout';
import useCreateWorkoutTemplate from 'hooks/workoutTemplate/useCreateWorkoutTemplate';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ScoreTypeEnum } from 'types/enums';
import { getAlphabetLetterByIndex } from 'utils/functions';
import notificationHandler from 'utils/notifications';

import { mapWorkoutToFormValues } from './mapWorkoutToFormValues';
import { createEmptySection, duplicateSection } from '../../../forms/workoutBuilder/createSection';
import {
  collectSectionErrors,
  extractMovementScoringPayload,
  hasMovementScoring,
} from '../../../forms/workoutBuilder/scoringFormHelpers';
import { WorkoutFormValues, WorkoutSectionData } from '../../../forms/workoutBuilder/types';
import { notifyWorkoutValidationErrors } from '../../../forms/workoutBuilder/workoutValidationToast';
import ModalWorkoutTemplatePreview, {
  WorkoutTemplatePreviewData,
} from '../../../Library/components/WorkoutLibrary/WorkoutTemplates/components/ModalWorkoutTemplatePreview';
import WorkoutSection from '../../../Library/pages/CreateWorkoutTemplate/components/WorkoutSection';
import { buildTemplatePayloadFromFormValues } from '../../components/AssignWorkoutModal/utils/templateToWorkoutWrite';
import type { ProgrammingNavState } from '../../components/types';

interface EditWorkoutLocationState {
  date: string;
  kiloClassTypeName?: string;
  view?: 'week' | 'agenda';
}

const useStyles = createStyles(theme => ({
  page: {
    height: '100%',
    overflowY: 'auto',
    backgroundColor: theme.colors.gray[0],
  },
  content: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '1.5rem',
    paddingBottom: 0,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    borderBottom: `0.0625rem solid ${theme.colors.gray[3]}`,
    backgroundColor: theme.white,
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  headerLeft: {
    flex: 1,
  },
  headerActions: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    flexShrink: 0,
  },
  previewLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: theme.colors.gray[7],
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,
    '&:hover': {
      color: theme.colors.gray[9],
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
  card: {
    marginBottom: '1rem',
    backgroundColor: theme.white,
    overflow: 'visible',
  },
  addSectionBar: {
    position: 'sticky',
    bottom: 0,
    backgroundColor: theme.colors.gray[0],
    paddingTop: '1rem',
    paddingBottom: '1.5rem',
    zIndex: 10,
  },
  addSectionButton: {
    width: '100%',
  },
}));

const EditWorkout = (): JSX.Element => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as EditWorkoutLocationState | null;
  const returnView = state?.view ?? 'agenda';

  const { workoutId } = useParams<{ workoutId: string }>();
  const { data: workoutResponse, isLoading, isError } = useWorkout(workoutId);
  const updateMutation = useEditAssignedWorkout(workoutId);
  const saveAsTemplateMutation = useCreateWorkoutTemplate();
  const { successNotification, errorNotification } = notificationHandler();
  const { hasPerm } = useAuth();
  const canMutate = hasPerm('WorkoutMutate');

  const [athleteNotesOpened, { toggle: toggleAthleteNotes }] = useDisclosure(false);
  const [coachNotesOpened, { toggle: toggleCoachNotes }] = useDisclosure(false);
  const [previewModalOpened, { open: openPreviewModal, close: closePreviewModal }] = useDisclosure(false);
  const { containerRef, scrollToBottom } = useScrollContainer<HTMLDivElement>();

  const workout = workoutResponse?.data;
  const assignedAthlete = workout?.athlete ?? null;
  const contextDate = state?.date ?? workout?.date;
  const contextLabel = assignedAthlete?.fullName ?? state?.kiloClassTypeName;
  const formattedDate = contextDate ? dayjs(contextDate).format('MMM D, YYYY') : '';
  const returnState: ProgrammingNavState = {
    view: returnView,
    date: contextDate,
    tab: assignedAthlete ? 'athletes' : 'classes',
    athleteId: assignedAthlete?.athleteId,
  };
  const form = useForm<WorkoutFormValues>({
    initialValues: {
      name: '',
      athleteNotes: '',
      coachNotes: '',
      isVisibleToAthletes: false,
      sections: [],
    },
    validate: {
      name: value => (value.trim().length === 0 ? 'Workout name is required' : null),
    },
  });

  useShallowEffect(() => {
    if (!workout || !workoutId || workout.workoutId !== workoutId) return;
    form.setValues(mapWorkoutToFormValues(workout));
  }, [workout, workoutId]);

  useEffect(() => {
    if (!workoutId && !isLoading) {
      navigate('/workouts/programming', { state: { view: returnView, date: state?.date }, replace: true });
    }
  }, [workoutId, isLoading, navigate, returnView, state?.date]);

  const updateSectionLetters = (sections: WorkoutSectionData[]): WorkoutSectionData[] =>
    sections.map((section, index) => ({
      ...section,
      letter: getAlphabetLetterByIndex(index),
    }));

  const handleAddSection = (): void => {
    const currentSections = form.values.sections;
    const nextLetter = getAlphabetLetterByIndex(currentSections.length);
    const newSection = createEmptySection({ letter: nextLetter });
    form.setFieldValue('sections', updateSectionLetters([...currentSections, newSection]));
    scrollToBottom();
  };

  const handleDuplicateSection = (index: number): void => {
    const currentSections = form.values.sections;
    const sectionToDuplicate = currentSections[index];
    const duplicatedSection = duplicateSection(sectionToDuplicate);
    const newSections = [
      ...currentSections.slice(0, index + 1),
      duplicatedSection,
      ...currentSections.slice(index + 1),
    ];
    form.setFieldValue('sections', updateSectionLetters(newSections));
    successNotification('Section duplicated', 'The section has been duplicated.');
  };

  const handleDeleteSection = (index: number): void => {
    if (form.values.sections.length > 1) {
      const newSections = form.values.sections.filter((_, i) => i !== index);
      form.setFieldValue('sections', updateSectionLetters(newSections));
    }
  };

  const validateWorkout = (actionLabel: string): boolean => {
    const { errors: nameErrors } = form.validate();
    const sectionErrors = collectSectionErrors(form.values.sections, form.setFieldError);
    const allErrors = { ...nameErrors, ...sectionErrors };
    if (Object.keys(allErrors).length === 0) return true;
    notifyWorkoutValidationErrors(errorNotification, allErrors, form.values.sections, actionLabel);
    return false;
  };

  const handleSave = (): void => {
    if (!validateWorkout('save')) return;
    if (!workoutId) return;

    const values = form.values;
    const payload = {
      name: values.name.trim(),
      athleteNotes: values.athleteNotes?.trim() || null,
      coachNotes: values.coachNotes?.trim() || null,
      isVisibleToAthletes: values.isVisibleToAthletes,
      sections: values.sections.map((section, sectionIndex) => ({
        name: section.name.trim(),
        workoutInstructions: section.description.trim(),
        orderIndex: sectionIndex,
        levelScaled: section.levelScaled,
        levelRx: section.levelRx,
        levelRxPlus: section.levelRxPlus,
        scoreEntireSection: section.scoreEntireSection,
        movements: (section.scoreEntireSection
          ? section.movements.filter(m => m.scoreType !== null)
          : section.movements.filter(hasMovementScoring)
        ).map((m, movementIndex) => ({
          workoutMovement: section.scoreEntireSection ? m.movementId || null : m.movementId,
          saveAsBenchmark: section.saveAsBenchmark,
          orderIndex: movementIndex,
          ...extractMovementScoringPayload(m),
        })),
      })),
    };

    updateMutation.mutate(payload, {
      onSuccess: () => {
        successNotification('Workout updated', 'Your changes have been saved.');
        navigate('/workouts/programming', { state: returnState });
      },
      onError: error => {
        const errorMessage = error.response?.data?.message ?? 'Failed to save workout';
        errorNotification('Save failed', errorMessage);
      },
    });
  };

  const handleSaveAsTemplate = (): void => {
    if (!validateWorkout('save as template')) return;
    const payload = buildTemplatePayloadFromFormValues(form.values);
    saveAsTemplateMutation.mutate(payload, {
      onSuccess: () => successNotification('Template saved', `"${form.values.name}" saved as a template.`),
      onError: error => {
        const msg = error.response?.data?.message ?? 'Failed to save template';
        errorNotification('Save failed', msg);
      },
    });
  };

  const handleClose = (): void => {
    navigate('/workouts/programming', { state: returnState });
  };

  const convertFormToPreviewData = (): WorkoutTemplatePreviewData => ({
    workoutTemplateId: undefined,
    name: form.values.name || 'Untitled Workout',
    athleteNotes: form.values.athleteNotes || null,
    coachNotes: form.values.coachNotes || null,
    sections: form.values.sections
      .filter(section => section.name || section.description)
      .map(section => {
        const synthMovementName = section.scoreEntireSection ? section.name.trim() || null : null;
        return {
          name: section.name || section.letter,
          workoutInstructions: section.description || '',
          movements: section.movements
            .filter(m => m.scoreType !== null && (synthMovementName || (m.movementId && m.movementName)))
            .map(m => ({
              workoutMovement: {
                workoutMovementId: m.movementId,
                name: synthMovementName ?? m.movementName ?? 'Unknown Movement',
              },
              scoreType: m.scoreType as ScoreTypeEnum,
            })),
        };
      }),
  });

  if (!workoutId) {
    return (
      <>
        <Text>No workout ID</Text>
      </>
    );
  }

  if (isLoading || !workout) {
    return (
      <Box className={classes.page}>
        <Box className={classes.content} sx={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <Loader size='lg' />
        </Box>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box className={classes.page}>
        <Box className={classes.content} p='xl'>
          <Text color='dimmed' mb='md'>
            Workout could not be loaded.
          </Text>
          <Button
            variant='subtle'
            onClick={() =>
              navigate('/workouts/programming', { state: { view: returnView, date: state?.date } })
            }
          >
            Back to Programming
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <ModalWorkoutTemplatePreview
        opened={previewModalOpened}
        template={convertFormToPreviewData()}
        handleClose={closePreviewModal}
        onEdit={closePreviewModal}
        showEditButton={false}
      />
      <Box className={classes.page} ref={containerRef}>
        <Box className={classes.content}>
          <header className={classes.header}>
            <div className={classes.headerLeft}>
              <Group spacing='sm' align='center' mb='xs'>
                <Title order={1}>Edit Workout</Title>
                {contextLabel && formattedDate && (
                  <Badge color='blue' variant='light' size='lg' data-cy='editWorkoutContext'>
                    {contextLabel} · {formattedDate}
                  </Badge>
                )}
              </Group>
              <Text color='dimmed' size='sm'>
                {assignedAthlete
                  ? 'Update this workout for the assigned athlete and date.'
                  : 'Update this workout for the assigned class and date.'}
              </Text>
            </div>
            <div className={classes.headerActions}>
              <Switch
                label={assignedAthlete ? 'Visible to athlete' : 'Visible to athletes'}
                checked={form.values.isVisibleToAthletes}
                onChange={e => form.setFieldValue('isVisibleToAthletes', e.currentTarget.checked)}
              />
              <UnstyledButton
                className={classes.previewLink}
                onClick={openPreviewModal}
                aria-label='Preview'
              >
                <IconDeviceDesktop size={18} />
                <Text component='span'>Preview</Text>
              </UnstyledButton>
              <Button
                variant='default'
                onClick={handleSaveAsTemplate}
                loading={saveAsTemplateMutation.isLoading}
                disabled={!canMutate || saveAsTemplateMutation.isLoading}
              >
                {saveAsTemplateMutation.isLoading ? 'Saving…' : 'Save as Template'}
              </Button>
              <Button
                onClick={handleSave}
                loading={updateMutation.isLoading}
                disabled={!canMutate || updateMutation.isLoading}
              >
                {updateMutation.isLoading ? 'Saving…' : 'Save changes'}
              </Button>
              <ActionIcon onClick={handleClose} size='lg' variant='subtle' color='gray' aria-label='Close'>
                <IconX size={20} />
              </ActionIcon>
            </div>
          </header>

          <Stack spacing='md'>
            <Card className={classes.card} p='md' radius='md' withBorder shadow='sm'>
              <Stack spacing='md'>
                <TextInput
                  label='Workout Name'
                  placeholder='Enter workout name'
                  required
                  {...form.getInputProps('name')}
                />
                <ExpandableSection
                  title={
                    <Text size='sm' weight={600}>
                      Athlete Notes
                    </Text>
                  }
                  opened={athleteNotesOpened}
                  handleToggle={toggleAthleteNotes}
                >
                  <WorkoutRichTextEditor
                    value={form.values.athleteNotes ?? ''}
                    onChange={html => form.setFieldValue('athleteNotes', html)}
                    error={form.getInputProps('athleteNotes').error as string | undefined}
                  />
                </ExpandableSection>
                <ExpandableSection
                  title={
                    <Group spacing='xs'>
                      <Text size='sm' weight={600}>
                        Coach Notes
                      </Text>
                      <Group spacing={4}>
                        <IconLock size={14} color='gray' />
                        <Text size='sm' color='dimmed'>
                          (Private)
                        </Text>
                      </Group>
                    </Group>
                  }
                  opened={coachNotesOpened}
                  handleToggle={toggleCoachNotes}
                >
                  <Textarea
                    placeholder='Internal notes for coaches only...'
                    {...form.getInputProps('coachNotes')}
                    minRows={3}
                  />
                </ExpandableSection>
              </Stack>
            </Card>

            {form.values.sections.map((section, index) => (
              <WorkoutSection
                key={section.id}
                section={section}
                index={index}
                form={form}
                onDelete={handleDeleteSection}
                onDuplicate={handleDuplicateSection}
                canDelete={form.values.sections.length > 1}
              />
            ))}
          </Stack>

          <Box className={classes.addSectionBar}>
            <Button
              className={classes.addSectionButton}
              leftIcon={<IconPlus size={16} />}
              onClick={handleAddSection}
            >
              Add Section
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default EditWorkout;
